import React, { useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TextInput, DateInput, PhoneNumberInput } from "artisn-ui-react";
import { PhoneNumberInputValues } from "artisn-ui-react";
import { convertDataToDataSelect } from "@anefi/utils";
import { useFetchActivityTypes, useFetchNationalities } from "@anefi/services";
import { useFetchUser } from "@anefi/services";
import { useCountries } from "@anefi/contexts";

import Styles from "./StepOneLegalForm.styles";
import { StepOneLegalFormProps as Props } from "./StepOneLegalForm.types";
import { StepOneLegalFormValues } from "./StepOneLegalForm.types";
import useI18n from "hooks/useI18n";
import Select from "components/global/Select/Select";
import useAuth from "contexts/auth/auth.context.hooks";
import FormDropdown from "components/global/FormDropdown/FormDropdown";

const StepOneLegalForm: React.FC<Props> = props => {
  const t = useI18n();
  const { register, formState, getValues, control } =
    useFormContext<StepOneLegalFormValues>();
  const { data: activityTypesData } = useFetchActivityTypes();
  const { data: nationalities } = useFetchNationalities();
  const auth = useAuth();
  const { data: user } = useFetchUser(auth);
  const { additionalInfo } = user ?? {};
  const { countryISOCodes, selectedCountry } = useCountries();
  const { isoCode: defaultCountryISOCode } = selectedCountry;
  const [phone, setPhone] = useState<PhoneNumberInputValues>({
    countryCallingCode: "",
    countryCode: defaultCountryISOCode,
    international: "",
    internationalRaw: "",
    isPossible: true,
    isValid: true,
    national: getValues().phone ?? "",
    nationalRaw: ""
  });
  const { businessActivityTypeCode } = additionalInfo ?? {};

  const activityTypesForSelect = useMemo(
    () => convertDataToDataSelect(activityTypesData, "code", "name"),
    [activityTypesData]
  );
  const defaultActivityType = useMemo(() => {
    return activityTypesForSelect.filter(
      activityTypes => activityTypes.key === businessActivityTypeCode
    )[0];
  }, [activityTypesForSelect, businessActivityTypeCode]);

  const sanitizedNationalities = useMemo(
    () => nationalities?.filter(nationality => nationality.name !== null),
    [nationalities]
  );
  const nationalitiesForSelect = useMemo(
    () => convertDataToDataSelect(sanitizedNationalities, "code", "name"),
    [sanitizedNationalities]
  );

  const errors = formState?.errors ?? {};
  const { businessName } = errors;
  const { businessActivityTypeCode: businessActivityError } = errors;
  const { documentTypeCode, document } = errors;
  const { nationalityCountryCode, startDate } = errors;
  const { phone: phoneError, politicallyExposed } = errors;

  return (
    <Styles className="StepOneLegalForm">
      <p className="StepOneLegalForm__title">
        Completa los datos para iniciar tu primera inversión
      </p>
      <TextInput
        {...register("businessName")}
        className="field"
        label={t.legalStepOneForm.businessName}
        placeholder={t.legalStepOneForm.businessName}
        maxLength={50}
        errorMessage={businessName?.message}
        disabled={additionalInfo?.businessName}
      />
      <Controller
        name="businessActivityTypeCode"
        control={control}
        render={({ field }) => (
          <FormDropdown
            {...field}
            className="StepOneLegalForm__select"
            selectData={activityTypesForSelect}
            label={t.stepSevenForm.businessActivityTypeCode}
            errorMessage={businessActivityError?.message}
            defaultValue={businessActivityTypeCode}
            defaultOption={defaultActivityType?.value}
            onChange={e => {
              field.onChange(e);
            }}
            placeholder="Selecciona una actividad"
          />
        )}
      />
      <p className="StepOneForm__text">{t.commonFieldForm.documentType}</p>
      <div className="radioContainer">
        <div className="radio radio__disabled">
          <input
            {...register("documentTypeCode", { required: true })}
            type="radio"
            value="1"
            id="1"
            disabled
          />
          <label htmlFor="site">{t.commonFieldForm.document}</label>
        </div>
        <div className="radio radio__disabled">
          <input
            {...register("documentTypeCode", { required: true })}
            type="radio"
            value="2"
            id="2"
            disabled
          />
          <label htmlFor="remote">{t.commonFieldForm.ruc}</label>
        </div>
        {documentTypeCode?.message ? (
          <span id="gender-error" className="errorMessage">
            {documentTypeCode?.message}
          </span>
        ) : null}
      </div>
      <TextInput
        {...register("document")}
        className="field"
        label={t.commonFieldForm.documentLabel}
        placeholder="0000000000"
        maxLength={50}
        errorMessage={document?.message}
        disabled
      />
      <Controller
        name="nationalityCountryCode"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="field"
            selectData={nationalitiesForSelect}
            label={t.stepOneForm.nationality}
            placeholder={t.stepOneForm.nationality}
            disabled={!!additionalInfo?.nationalityCountryCode}
            errorMessage={nationalityCountryCode?.message}
          />
        )}
      />
      <p className="StepOneForm__text">{t.stepOneForm.expose}</p>
      <div className="radioContainer">
        <div className="radio">
          <input
            {...register("politicallyExposed", { required: true })}
            defaultChecked={additionalInfo?.politicallyExposed === "NO"}
            type="radio"
            value="NO"
            id="NO"
          />
          <label htmlFor="site">No</label>
        </div>
        <div className="radio">
          <input
            {...register("politicallyExposed", { required: true })}
            defaultChecked={additionalInfo?.politicallyExposed === "DIR"}
            type="radio"
            value="DIR"
            id="DIR"
          />
          <label htmlFor="remote">Directo</label>
        </div>
        <div className="radio">
          <input
            {...register("politicallyExposed", { required: true })}
            defaultChecked={additionalInfo?.politicallyExposed === "IND"}
            type="radio"
            value="IND"
            id="IND"
          />
          <label htmlFor="remote">Indirecto</label>
        </div>
        {politicallyExposed?.message ? (
          <span id="gender-error" className="errorMessage">
            {politicallyExposed?.message}
          </span>
        ) : null}
      </div>
      <Controller
        name="phone"
        control={control}
        render={({ field }) => (
          <PhoneNumberInput
            {...field}
            className="field"
            label={t.commonFieldForm.phone}
            countriesCodes={countryISOCodes}
            onChange={e => {
              setPhone(e);
              field.onChange(e.national);
            }}
            errorMessage={phoneError?.message}
            inputValue={phone.national}
            maxLength={12}
            placeholder="099 999 9999"
          />
        )}
      />
      <DateInput
        {...register("startDate")}
        label={t.legalStepOneForm.startDate}
        className="field"
        errorMessage={startDate?.message}
        disabled={additionalInfo?.startDate}
      />
    </Styles>
  );
};

StepOneLegalForm.defaultProps = {};

export default StepOneLegalForm;
